<template>
  <section>
    <cso-component-state :componentState="state"
                        :customError="error">
      <template v-slot:ready>
        <cso-status-page-for-digital-interface
            name="KUNDENDOKUMENTE"
            :prefilled-prozesse="prozesse">
          <cso-box class="cso-box--margin">
            <div class="doc-header">
              <span class="date-header">{{ $t('postboxProcess.table.date') }}</span>
              <span class="name-header">{{ $t('postboxProcess.table.name') }}</span>
              <span class="file-header">{{ $t('postboxProcess.table.documents') }}</span>
            </div>
            <div class="tab">
              <div v-for="(document, index) in documents.slice(0,6)"
                  :key="index"
                  class="panel">
                <div class="date-column">
                  <span>{{ document.date }}</span>
                </div>
                <div class="name-column">
                  <span>{{ document.name }}</span>
                </div>
                <div class="doc-column"
                    v-if="document.documentId && document.systemId">
                  <div class="download">
                    <a @click="fetchPdf(document)" class="standard-link">{{ $t('postboxProcess.table.showPdf') }}</a>
                    <a @click="downloadPdf(document)" class="standard-link">{{ $t('postboxProcess.table.downloadPdf') }}</a>
                  </div>
                  <cso-link :link="StaticUrls.sspCorrectInvoice"
                            v-if="document.invoiceCorrectionLink"
                            :new-tab="true"
                            :label="$t('postboxProcess.table.correctInvoice')">
                  </cso-link>
                </div>
              </div>
              <cso-button v-if="documents.length > 6"
                          modifier="custom"
                          :label="$t('general.showMoreRows')"
                          @click="expanded = !expanded">
              </cso-button>
            </div>
            <cso-grid-column v-if="expanded"
                            class="tab">
              <div v-for="(document, index) in documents.slice(6,100000)"
                  :key="index"
                  class="panel">
                <div class="date-column">
                  <span>{{ document.date }}</span>
                </div>
                <div class="name-column">
                  <span>{{ document.name }}</span>
                </div>
                <div class="doc-column"
                    v-if="document.documentId && document.systemId">
                  <div class="download">
                    <a @click.stop.prevent="fetchPdf(document)" class="standard-link">{{ $t('postboxProcess.table.showPdf') }}</a>
                    <a @click.stop.prevent="downloadPdf(document)" class="standard-link">{{ $t('postboxProcess.table.downloadPdf') }}</a>
                  </div>
                  <cso-link :link="StaticUrls.sspCorrectInvoice"
                            v-if="document.invoiceCorrectionLink"
                            :new-tab="true"
                            :label="$t('postboxProcess.table.correctInvoice')">
                  </cso-link>
                </div>
              </div>
            </cso-grid-column>
          </cso-box>
          <section class="cso-page-structure">
            <cso-faq name="FAQ_POSTFACH" />
          </section>
        </cso-status-page-for-digital-interface>
      </template>
    </cso-component-state>
  </section>
</template>

<script lang="ts">
import store from '@/store'

import di from '@/api/digital-interface'
import * as DI from '@/api/digital-interface/client'
import ComponentsStates from '@/common/ComponentsStates'
import StaticUrls from '@/common/StaticUrls'
import Utils from '@/common/Utils'
import LoggingService from '@/common/LoggingService'

import CsoStatusPageForDigitalInterface from '@/components/core/business/CsoStatusPageForDigitalInterface.vue'
import CsoComponentState from '@/components/core/composition/CsoComponentState.vue'

interface Document {
  name: string
  date: string
  documentId: string
  systemId: string
  invoiceCorrectionLink: boolean
}

interface ComponentData {
  documents: Document[]
  state: string
  expanded: boolean
  error: string
  prozesse: DI.V13.Prozesse
  StaticUrls: typeof StaticUrls
  vk: string
}

const PROCESS = 'RECHNUNGSKORREKTUR'
const MAIN_PROCESS = 'KUNDENDOKUMENTE'

export default {
  name: 'CsoPostboxRoutePage',

  components: {
    CsoComponentState,
    CsoStatusPageForDigitalInterface
  },

  data (): ComponentData {
    return {
      documents: [],
      state: ComponentsStates.loading,
      expanded: false,
      prozesse: DI.V13.Prozesse.createEmpty(),
      StaticUrls,
      error: '',
      vk: ''
    }
  },

  methods: {
    async fetchDocuments (vk: string) {
      if (!this.prozesse.isEnabled(MAIN_PROCESS)) {
        this.state = ComponentsStates.ready
        return
      }
      try {
        const kundendokumente = await di.v13.getKundendokumente(vk)
        this.documents = kundendokumente.Kundendokumente.map((dokument) => ({
          name: dokument.Name,
          date: Utils.dateToLocale(dokument.Erstelldatum),
          documentId: dokument.ArchiviertesDokument?.Dokument.DateiId,
          systemId: dokument.ArchiviertesDokument?.Dokument.SystemId,
          invoiceCorrectionLink: this.isInvoiceCorrectionLinkEnabled(dokument)
        }))
        if (this.documents.length) {
          this.state = ComponentsStates.ready
        } else {
          this.state = ComponentsStates.error
          this.error = kundendokumente.Meldungen
        }
      } catch (error) {
        this.state = ComponentsStates.error
      }
    },

    async fetchPdf (document: Document) {
      this.state = ComponentsStates.loading
      try {
        const file = await di.v13.getDatei(document.systemId, document.documentId)
        const fileUrl = URL.createObjectURL(file)
        Utils.openInNewTabOrDownload(fileUrl, file)
        this.state = ComponentsStates.ready
      } catch (error) {
        LoggingService.log(LoggingService.eventTypes.userEvent, 'Postbox Error: ' + error)
        this.state = ComponentsStates.error
      }
    },

    async downloadPdf (document: Document) {
      this.state = ComponentsStates.loading
      try {
        const file = await di.v13.getDatei(document.systemId, document.documentId)
        const fileWithNiceName = new File([file], document.name, { type: file.type })
        Utils.downloadFile(fileWithNiceName)
        this.state = ComponentsStates.ready
      } catch (error) {
        LoggingService.log(LoggingService.eventTypes.userEvent, 'Postbox Error: ' + error)
        this.state = ComponentsStates.error
      }
    },

    isInvoiceCorrectionLinkEnabled(document) {
      if (this.prozesse.isEnabled(PROCESS) && !!this.prozesse.getWerte(PROCESS, 'ID_RECHNUNG')) {
        return document.ArchiviertesDokument?.Dokument?.DateiId === this.prozesse.getWerte(PROCESS, 'ID_RECHNUNG')
      }
      return false
    },

    async getProzesse (vk: string) {
      this.prozesse = await di.v13.getProzesse([vk], [MAIN_PROCESS, PROCESS])
    }
  },

  async created () {
    this.$emit('banner', { title: this.$t('postboxProcess.banner') })
    if (!store.vk || store.vk === '') {
      this.$router.push({ name: 'addBusinessPartner', query: this.$route.query })
    } else {
      await this.getProzesse(store.vk)
      await this.fetchDocuments(store.vk)
    }
  }
}
</script>

<style scoped lang="scss">
.tab {
  display: contents;
  button {
    margin-bottom: 15px;
  }
}

.doc-header {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 15px;
  flex: 1 1 100%;

  .date-header {
    width: 15%;

    @include lg {
      width: 100px;
    }

    @include sm {
      display: none;
    }
  }
  .name-header {
    width: 45%;

    @include md {
      width: 35%;
    }

    @include sm {
      display: none;
    }
  }
  .file-header {
    width: 35%;

    @include md {
      width: 50%;
    }

    @include sm {
      display: none;
    }
  }
}
.panel {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 15px;
  flex: 1 1 100%;
  align-items: center;

  @include sm {
    display: flex;
    flex-direction: column;
  }
  .date-column {
    width: 15%;

    @include lg {
      width: 100px;
    }

    @include sm {
      flex: 1 1 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-self: center;
      align-items: center;
      padding: 5px;
      min-height: 25px;
    }
  }
  .name-column {
    width: 45%;

    @include md {
      width: 35%;
    }

    @include sm {
      flex: 1 1 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-self: center;
      align-items: center;
      padding: 5px;
      min-height: 25px;
    }
    span {
      @include sm {
        text-align: center;
      }
    }
  }
  .doc-column {
    width: 40%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @include md {
      width: 50%;
    }

    @include sm {
      flex: 1 1 100%;
      flex-direction: column;
      width: 100%;
      justify-content: center;
      align-self: center;
      padding: 5px;
      min-height: 25px;

      .download {
        text-align: center;
      }
    }

    .download a {
      cursor: pointer;
      display: block;
    }

    i {
      vertical-align: sub;
      cursor: pointer;
    }

    a {
      color: $blue;

      &:hover {
        text-decoration: underline;
      }

      @include sm {
        padding-top: 5px;
      }
    }
  }
}
.panel:nth-child(odd) {
  background-color: $gray-light;
}
.panel:nth-child(even) {
  background-color: $white;
}
</style>
<style lang="scss">
.panel .doc-column a span.label-url {
  font-family: $font-family-light;
}
</style>
